import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDown, EyeOff, GripVertical, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Checkbox } from '@/components/ui/Checkbox';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export interface SortablePatientTab {
  key: string;
  label: ReactNode;
  active: boolean;
  onSelect: () => void;
  /** Plain-text name for screen-reader drag announcements. */
  name: string;
  className?: string;
  /** Hidden by the user but shown because it is the active tab (e.g. a ?tab= deep link). */
  temporary?: boolean;
  count?: number;
}

export interface PatientTabVisibilityOption {
  key: string;
  name: string;
  shown: boolean;
  /** The first tab: always shown, checkbox disabled. */
  locked: boolean;
}

/**
 * Patient tab bar: Flere (n) overflow on desktop, fading scroll under 760,
 * plus Tilpas faner (visibility, drag-to-reorder).
 */
export function SortablePatientTabs({
  tabs,
  onReorder,
  visibilityOptions,
  onToggleVisible,
  canReset,
  onReset,
}: {
  tabs: SortablePatientTab[];
  onReorder: (activeKey: string, overKey: string) => void;
  visibilityOptions: PatientTabVisibilityOption[];
  onToggleVisible: (key: string, shown: boolean) => void;
  canReset: boolean;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const activeKey = tabs.find((tab) => tab.active)?.key;

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;

    const update = () => {
      const toolsWidth = 140;
      const available = row.clientWidth - toolsWidth;
      if (available <= 0) {
        setVisibleCount(tabs.length);
        return;
      }
      const kids = Array.from(measure.children) as HTMLElement[];
      let used = 0;
      let count = tabs.length;
      for (let i = 0; i < kids.length; i += 1) {
        used += kids[i].offsetWidth + 4;
        if (used > available) {
          count = Math.max(1, i);
          break;
        }
      }
      const activeIndex = tabs.findIndex((tab) => tab.active);
      if (activeIndex >= count) {
        count = Math.min(tabs.length, activeIndex + 1);
      }
      setVisibleCount(count);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [tabs, activeKey]);

  const visible = tabs.slice(0, visibleCount);
  const overflow = tabs.slice(visibleCount);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
      return;
    }
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.active);
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else next = tabs.length - 1;
    tabs[next]?.onSelect();
  };

  return (
    <div className="relative">
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex h-0 overflow-hidden"
      >
        {tabs.map((tab) => (
          <span key={tab.key} className={cn(TYPE.control, 'inline-flex h-11 px-s3')}>
            {tab.name}
            {tab.count != null ? ` ${tab.count}` : ''}
          </span>
        ))}
      </div>

      <div className="flex items-end gap-s4 border-b border-ds-line">
        <div
          ref={rowRef}
          role="tablist"
          aria-label={t('patients.header.tabs_label')}
          onKeyDown={onKeyDown}
          className="hidden min-w-0 flex-1 items-end nav:flex"
        >
          {visible.map((tab) => (
            <PatientTabButton key={tab.key} tab={tab} />
          ))}
          {overflow.length > 0 ? (
            <OverflowMenu
              tabs={overflow}
              label={t('design.more_tabs', { count: overflow.length })}
              activeKey={tabs.find((tab) => tab.active)?.key ?? ''}
            />
          ) : null}
        </div>

        <div
          role="tablist"
          aria-label={t('patients.header.tabs_label')}
          onKeyDown={onKeyDown}
          className="flex min-w-0 flex-1 overflow-x-auto scrollbar-hide [mask-image:linear-gradient(to_right,black_80%,transparent)] nav:hidden"
        >
          {tabs.map((tab) => (
            <PatientTabButton key={tab.key} tab={tab} />
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-s1 pb-s1">
          <TabCustomizeMenu
            options={visibilityOptions}
            onToggleVisible={onToggleVisible}
            canReset={canReset}
            onReset={onReset}
            onReorder={onReorder}
          />
        </div>
      </div>
    </div>
  );
}

function PatientTabButton({ tab }: { tab: SortablePatientTab }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="tab"
      aria-selected={tab.active}
      aria-current={tab.active ? 'page' : undefined}
      tabIndex={tab.active ? 0 : -1}
      onClick={tab.onSelect}
      className={cn(
        TYPE.control,
        'relative inline-flex h-11 shrink-0 items-center px-s3',
        tab.active ? 'font-semibold text-ds-teal' : 'font-medium text-ds-muted hover:text-ds-text',
      )}
    >
      {tab.label}
      {tab.temporary ? (
        <span title={t('patients.tab_order.temporary_hint')} className="ml-s1 text-ds-muted">
          <EyeOff className="h-3 w-3" aria-hidden />
          <span className="sr-only">{t('patients.tab_order.temporary_hint')}</span>
        </span>
      ) : null}
      {tab.count != null ? (
        <span className="ml-s1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-ds-neutral-tint px-s1 text-[12px] font-semibold text-ds-muted">
          {tab.count}
        </span>
      ) : null}
      {tab.active ? (
        <span className="absolute inset-x-s3 -bottom-px h-0.5 rounded-sm bg-ds-teal" />
      ) : null}
    </button>
  );
}

function OverflowMenu({
  tabs,
  label,
  activeKey,
}: {
  tabs: SortablePatientTab[];
  label: string;
  activeKey: string;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
  }, [activeKey]);
  return (
    <div className="relative">
      <button
        type="button"
        className={cn(TYPE.control, 'more-tab inline-flex h-11 items-center gap-s1 px-s3 text-ds-muted')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-s1 min-w-[200px] rounded-pop border border-ds-line bg-ds-card p-s1 shadow-pop"
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="menuitem"
              onClick={() => {
                tab.onSelect();
                setOpen(false);
              }}
              className={cn(
                TYPE.control,
                'flex h-10 w-full items-center rounded-lg px-s2 text-left',
                tab.active ? 'bg-ds-teal-tint text-ds-teal' : 'text-ds-text hover:bg-ds-teal-tint',
              )}
            >
              {tab.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TabCustomizeMenu({
  options,
  onToggleVisible,
  canReset,
  onReset,
  onReorder,
}: {
  options: PatientTabVisibilityOption[];
  onToggleVisible: (key: string, shown: boolean) => void;
  canReset: boolean;
  onReset: () => void;
  onReorder: (activeKey: string, overKey: string) => void;
}) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  }

  return (
    <Popover className="relative shrink-0">
      <PopoverButton
        title={t('patients.tab_order.customize')}
        className={cn(
          TYPE.control,
          'inline-flex h-10 items-center gap-s1 rounded-ctl px-s2 text-ds-teal hover:bg-ds-teal-tint',
        )}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        <span className="hidden nav:inline">{t('patients.tab_order.customize')}</span>
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: 'bottom end', gap: 8 }}
        className="z-50 w-72 rounded-pop border border-ds-line bg-ds-card p-s3 shadow-pop focus:outline-none"
      >
        <p className={cn(TYPE.section, 'text-ds-text')}>{t('patients.tab_order.customize')}</p>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patients.tab_order.customize_hint')}</p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={options.map((option) => option.key)} strategy={verticalListSortingStrategy}>
            <ul className="mt-s3 max-h-80 space-y-0.5 overflow-y-auto">
              {options.map((option) => (
                <CustomizeRow
                  key={option.key}
                  option={option}
                  onToggleVisible={onToggleVisible}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <div className="mt-s3 flex items-center justify-between border-t border-ds-line pt-s2">
          <span className={cn(TYPE.caption, 'text-ds-muted')}>{t('patients.tab_order.drag_hint')}</span>
          <button
            type="button"
            onClick={onReset}
            disabled={!canReset}
            className={cn(
              TYPE.control,
              'inline-flex items-center gap-s1 rounded-ctl px-s2 py-s1 text-ds-teal hover:bg-ds-teal-tint disabled:cursor-default disabled:text-ds-muted',
            )}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            {t('patients.tab_order.reset_all')}
          </button>
        </div>
      </PopoverPanel>
    </Popover>
  );
}

function CustomizeRow({
  option,
  onToggleVisible,
}: {
  option: PatientTabVisibilityOption;
  onToggleVisible: (key: string, shown: boolean) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: option.key });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('flex h-10 items-center gap-s2 rounded-lg px-s1', isDragging && 'z-10 bg-ds-sunk')}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={t('patients.tab_order.move', { name: option.name })}
        className="inline-flex h-8 w-6 items-center justify-center rounded text-ds-muted"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      <Checkbox
        checked={option.shown}
        disabled={option.locked}
        onChange={(event) => onToggleVisible(option.key, event.target.checked)}
        label={option.name}
        className="min-h-8 flex-1"
      />
      {option.locked ? (
        <span className={cn(TYPE.caption, 'shrink-0 text-ds-muted')}>
          {t('patients.tab_order.always_shown')}
        </span>
      ) : null}
    </li>
  );
}
